/**
 * overlay.js — pure colour maps for the 3D overlays (CONTRACT §10.2).
 *
 *   paintSubtrees(tree, entries)       moved from ExplodedView.jsx; also used by the agent overlay
 *   agentHighlightColors(tree, action) the agent's view_highlight as node id -> hex
 *   leadPart(action)                   the part the stage selects and solos for a highlight
 *
 * No React, no three.js: Node runs __tests__/overlay.test.js directly.
 */

import { OVERLAY_MUTED, traceRankColor, answerRole } from './diagnosticPalette.js';

/**
 * Paint `entries` ([id, colour], highest priority first) onto each id AND its descendants:
 * meshes are owned by their nearest bound part, so colouring an S node alone would leave its
 * units in identity colours. Every other node gets OVERLAY_MUTED.
 */
export function paintSubtrees(tree, entries) {
  const map = new Map();
  for (const [id, color] of entries) {
    if (!tree.byId.has(id)) continue;
    if (!map.has(id)) map.set(id, color);
    for (const d of tree.descendantsOf(id)) if (!map.has(d)) map.set(d, color);
  }
  for (const id of tree.byId.keys()) if (!map.has(id)) map.set(id, OVERLAY_MUTED);
  return map;
}

const idsOf = (action) => {
  const ids = action?.part_ids ?? action?.ids ?? [];
  return Array.isArray(ids) ? ids.filter((id) => typeof id === 'string' && id) : [];
};

/** Colour of one highlighted id: explicit colour, else rank ramp, else answer-role colour. */
export function agentPartColor(action, id) {
  const colors = action?.colors;
  const ranks = action?.ranks;
  const roles = action?.roles;
  return colors?.[id] ?? (ranks?.[id] ? traceRankColor(ranks[id]) : answerRole(roles?.[id]).color);
}

/**
 * Node id -> hex for an agent highlight ({part_ids|ids, roles?, ranks?, colors?}).
 * Deeper ids are painted first so a part named inside a highlighted assembly keeps its own
 * colour; ties keep the agent's order. Returns null when no id is in the tree.
 */
export function agentHighlightColors(tree, action) {
  if (!tree?.byId) return null;
  const ids = idsOf(action).filter((id) => tree.byId.has(id));
  if (ids.length === 0) return null;
  const depth = (id) => (typeof tree.depthOf === 'function' ? tree.depthOf(id) : 0);
  const ordered = ids
    .map((id, i) => ({ id, i, d: depth(id) }))
    .sort((a, b) => (b.d - a.d) || (a.i - b.i))
    .map(({ id }) => [id, agentPartColor(action, id)]);
  return paintSubtrees(tree, ordered);
}

/** First id with role `cause`, else the rank-1 id, else the first id; null for no ids. */
export function leadPart(action) {
  const ids = idsOf(action);
  if (ids.length === 0) return null;
  const roles = action?.roles || {};
  const ranks = action?.ranks || {};
  const cause = ids.find((id) => String(roles[id] || '').toLowerCase() === 'cause');
  if (cause) return cause;
  const first = ids.find((id) => Number(ranks[id]) === 1);
  return first ?? ids[0];
}
